import React, { useCallback, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getShipment, calculateShipment, exportShipment } from 'services/shipments.service'
import StatusBadge from 'components/StatusBadge'
import SettingsPanel from 'components/shipment/SettingsPanel'
import DocumentsTab from 'components/shipment/DocumentsTab'
import ItemsTab from 'components/shipment/ItemsTab'
import CostLinesTab from 'components/shipment/CostLinesTab'
import ResultTab from 'components/shipment/ResultTab'
import ShipmentGuide from 'components/shipment/ShipmentGuide'
import { downloadBlob } from 'utils/format'
import { useI18n, apiError } from 'i18n'

const TABS = ['documents', 'items', 'costs', 'result']

const ShipmentDetail = () => {
  const { t, fmt } = useI18n()
  const { shipmentId } = useParams()
  const [shipment, setShipment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('documents')
  const [busy, setBusy] = useState(false)
  const [showSettings, setShowSettings] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await getShipment(shipmentId)
      setShipment(res.shipment)
    } catch (error) {
      toast.error(apiError(error, t('shipment.loadFailed')))
    } finally {
      setLoading(false)
    }
  }, [shipmentId])

  useEffect(() => { load() }, [load])

  const onCalculate = async () => {
    setBusy(true)
    try {
      const { result } = await calculateShipment(shipmentId)
      if (!result.ok) toast.error(t('shipment.calculatedWithErrors'))
      else toast.success(t('shipment.calculated'))
      await load()
      setTab('result')
    } catch (error) {
      toast.error(apiError(error, t('shipment.calculateFailed')))
    } finally {
      setBusy(false)
    }
  }

  const onExport = async (format) => {
    try {
      const blob = await exportShipment(shipmentId, format)
      downloadBlob(blob, `landed-cost-${shipment.reference}.${format}`)
    } catch (error) {
      toast.error(t('shipment.exportFailed'))
    }
  }

  if (loading) return <p className='text-slate-400'>{t('common.loading')}</p>
  if (!shipment) return <p className='text-slate-400'>{t('shipment.notFound')} <Link to='/shipments' className='text-brand'>{t('common.back')}</Link></p>

  const pending = shipment.costLines.filter((l) => l.status === 'suggested' && !l.duplicateOf).length
  const closed = shipment.status === 'closed'
  const counts = { documents: shipment.documents.length, items: shipment.items.length, costs: shipment.costLines.length }

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div>
          <Link to='/shipments' className='text-sm text-slate-500 hover:underline'>{t('shipment.back')}</Link>
          <div className='mt-1 flex flex-wrap items-center gap-3'>
            <h1 className='text-2xl font-bold'>{shipment.reference}</h1>
            <StatusBadge status={shipment.status} />
          </div>
          <p className='mt-1 text-sm text-slate-500'>
            {shipment.incoterm || t('shipment.noIncoterm')} ·{' '}
            {shipment.transportMode ? t(`transport.${shipment.transportMode}`) : t('shipment.noTransport')} ·{' '}
            {t(`modes.${shipment.mode}`)} · {shipment.reportCurrency}
            {shipment.exchangeRates && Object.entries(shipment.exchangeRates).map(([c, r]) => ` · ${c} ${fmt.number(r)}`)}
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <button onClick={() => setShowSettings((v) => !v)} className='rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50'>
            {showSettings ? t('shipment.hideSettings') : t('shipment.settings')}
          </button>
          {shipment.result && (
            <>
              <button onClick={() => onExport('csv')} className='rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50'>{t('shipment.exportCsv')}</button>
              <button onClick={() => onExport('json')} className='rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50'>{t('shipment.exportJson')}</button>
            </>
          )}
          <button onClick={onCalculate} disabled={busy || !shipment.items.length}
            className='rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600 disabled:opacity-50'>
            {busy ? t('shipment.calculating') : shipment.result ? t('shipment.recalculate') : t('shipment.calculate')}
          </button>
        </div>
      </div>

      {/* Remount on every reload so the form shows fresh rates (e.g. after a Banxico refresh). */}
      {showSettings && <SettingsPanel key={shipment.updatedAt} shipment={shipment} onSaved={load} />}

      {!closed && (
        <ShipmentGuide shipment={shipment} pending={pending} activeTab={tab} onGoTo={setTab} onCalculate={onCalculate} calculating={busy} />
      )}

      {pending > 0 && (
        <div className='rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-status-review'>
          {t('shipment.pending', { count: pending })}{' '}
          <button onClick={() => setTab('costs')} className='font-semibold underline'>{t('shipment.reviewCosts')}</button>
        </div>
      )}
      {shipment.status === 'review' && shipment.result && (
        <p className='text-sm text-slate-500'>{t('shipment.stale')}</p>
      )}

      <div className='overflow-x-auto'>
        <nav className='flex min-w-max gap-4 shadow-[inset_0_-1px_0_theme(colors.slate.200)] sm:gap-6'>
          {TABS.map((key) => (
            <button key={key} onClick={() => setTab(key)}
              className={`shrink-0 whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-semibold ${tab === key ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              {t(`shipment.tabs.${key}`)}{counts[key] !== undefined && <span className='ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500'>{counts[key]}</span>}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'documents' && <DocumentsTab shipment={shipment} readOnly={closed} onChange={load} />}
      {tab === 'items' && <ItemsTab shipment={shipment} readOnly={closed} onChange={load} />}
      {tab === 'costs' && <CostLinesTab shipment={shipment} readOnly={closed} onChange={load} />}
      {tab === 'result' && <ResultTab shipment={shipment} />}
    </div>
  )
}

export default ShipmentDetail
